import type { PromptsProps } from './antdx';

export type MessageType =
| 'TextMessage'
| 'ActionExecutionMessage'
| 'ResultMessage'
| 'AgentStateMessage'
| 'SuggestionMessage';

export enum MessageRole {
  user = 'user',      // 用户
  assistant = 'assistant', // AI助手
  suggestion = 'suggestion',  // 建议
  system = 'system',      // 工具
}

export enum MessageStatus {
  init = 'init',
  pending = 'pending', // 生成中/接收中
  done = 'done', // 已完成
  failed = 'failed', // 失败
  recalled = 'recalled', // 已撤回
  deleted = 'deleted', // 已删除
}

/**
 * 阶段性回答内容
 */
export interface Steps {
  id: string; // 阶段性回答ID
  content: any; // 文本内容
}

/**
* 所有消息的基础结构
*/
export interface BaseMessage {
  id: string; // 消息唯一ID
  type: MessageType; // 消息类型
  createdAt: Date;   // 创建时间
  status?: MessageStatus; // 内容是否生成中
  execute?: ActionExecutionMessage[]; // 智能体执行过程
  stepContent?: Steps[]; // 阶段性回答内容
  thinks?: string ; // 思考过程
  extra?: any; // 额外消息
  isLike?: 0 | 1 | -1; // 是否喜欢
  raw?: any; // 原始消息
}

export interface FileMessage {
  fileId?: string | number; // 文件ID
  fileName?: string; // 文件名
  fileSize?: number | string; // 文件大小（字节）
  fileType?: string; // 文件类型（MIME类型）
  fileUrl?: string; // 文件URL（可选，用于展示/下载）
}

/**
* 文本消息
*/
export interface TextMessage extends BaseMessage {
  type: 'TextMessage';
  role: MessageRole; // 发送者角色
  content: {
    text?: string;  // 文本内容
    files?: FileMessage[]; // 文件列表
  };
  parentMessageId?: string; // 父消息ID（可选）
}

/**
* 工具/动作执行消息
*/
export interface ActionExecutionMessage extends BaseMessage {
  type: 'ActionExecutionMessage';
  name: string; // 动作/工具名称
  arguments?: Record<string, any>; // 执行参数
  parentMessageId?: string;
  extra?: any; // 额外信息
}

/**
* 工具/动作执行结果消息
*/
export interface ResultMessage extends BaseMessage {
  type: 'ResultMessage';
  actionExecutionId: string; // 执行ID
  actionName: string; // 动作/工具名称
  result: string; // 执行结果
}

/**
* 智能体状态消息
*/
export interface AgentStateMessage extends BaseMessage {
  type: 'AgentStateMessage';
  threadId: string; // 线程ID
  agentName: string; // agent名称
  nodeName: string; // 节点名称
  runId: string; // 运行ID
  active: boolean; // 是否激活
  role: MessageRole; // 角色
  state: any; // 状态内容
  running: boolean; // 是否运行中
}

/**
 * 提示消息
 */
export interface SuggestionMessage extends BaseMessage {
  type: 'SuggestionMessage';
  content: PromptsProps['items'];
  role: MessageRole;
}

/**
* 消息输入类型（用于前端输入/组装）
* 可选地包含不同类型的消息体
*/
export interface MessageInput extends BaseMessage {
  textMessage?: Omit<TextMessage, 'id' | 'createdAt' | 'type'>;
  actionExecutionMessage?: Omit<ActionExecutionMessage, 'id' | 'createdAt' | 'type'>;
  resultMessage?: Omit<ResultMessage, 'id' | 'createdAt' | 'type'>;
  agentStateMessage?: Omit<AgentStateMessage, 'id' | 'createdAt' | 'type'>;
}

/**
* 消息列表中消息格式
*/
export type Messages = TextMessage | SuggestionMessage;

export enum ActionAvailability {
  disabled = 'disabled', // 不可用
  enabled = 'enabled',   // 可用
  remote = 'remote',     // 远程可用
}

/**
* 工具/动作定义
*/
export interface Action {
  name: string; // 名称
  description: string; // 描述
  jsonSchema: string; // 参数JSON Schema
  available?: ActionAvailability; // 可用性
}
